import { Store, User, MapPin, Phone, PackageOpen, Calendar } from "lucide-react";
import { PackageStatusBadge } from "@/components/StatusBadge";

export default function ScannedPackageDetail({ qr, pkg }) {
  return (
    <div className="space-y-4 mt-2">
      <div className="flex items-center gap-3 border border-slate-200 dark:border-slate-800 rounded-lg p-3 bg-slate-100/40 dark:bg-slate-900/40">
        <div className="bg-white p-1.5 rounded">
          <img src={qr.qr_image} alt={qr.qr_code_id} className="w-16 h-16" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono-tech text-xs text-cyan-300">{qr.qr_code_id}</div>
          {pkg?.package_id && <div className="font-mono-tech text-xs text-slate-600 dark:text-slate-400">{pkg.package_id}</div>}
          <div className="mt-1.5"><PackageStatusBadge status={pkg?.status || qr.status} /></div>
        </div>
      </div>

      {pkg && (
        <>
          <Section title="Package">
            <div className="font-display font-semibold text-base text-slate-900 dark:text-white">{pkg.package_name}</div>
            {pkg.description && <div className="text-sm text-slate-600 dark:text-slate-400 mt-0.5">{pkg.description}</div>}
            {pkg.special_instructions && (
              <div className="mt-2 p-2 rounded border border-yellow-500/30 bg-yellow-500/5 text-xs text-yellow-100">
                <b>Instructions:</b> {pkg.special_instructions}
              </div>
            )}
          </Section>

          <Section title="Items">
            <ul className="space-y-1.5 text-sm">
              {(pkg.items || []).map((it, i) => (
                <li key={i} className="flex justify-between border-b border-slate-200/60 dark:border-slate-800/60 pb-1.5 last:border-0">
                  <span className="text-slate-200">{it.item_name}</span>
                  <span className="text-cyan-300 font-mono-tech text-xs">×{it.quantity}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="Receiver">
            <div className="text-slate-900 dark:text-white font-medium">{pkg.receiver_name}</div>
            <div className="text-sm text-slate-600 dark:text-slate-400 flex items-center gap-1.5 mt-0.5">
              <Phone className="w-3.5 h-3.5" /> {pkg.receiver_phone}
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-400 flex items-start gap-1.5 mt-0.5">
              <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{pkg.receiver_address}</span>
            </div>
          </Section>

          <div className="grid grid-cols-2 gap-3">
            <Section title="Store" icon={Store}>
              <div className="text-sm text-slate-900 dark:text-white">{pkg.store_name}</div>
              <div className="text-xs text-slate-500">{pkg.store_owner_name}</div>
            </Section>
            <Section title="Created" icon={Calendar}>
              <div className="text-xs text-slate-700 dark:text-slate-300">{new Date(pkg.created_at).toLocaleString()}</div>
              <div className="text-[10px] text-slate-500 mt-1">
                Updated {new Date(pkg.updated_at).toLocaleString()}
              </div>
            </Section>
          </div>
        </>
      )}

      {!pkg && (
        <div className="text-center text-sm text-slate-600 dark:text-slate-400 py-6">
          <PackageOpen className="w-8 h-8 mx-auto text-slate-500 dark:text-slate-600 mb-2" />
          This QR has no package information yet.
        </div>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="border border-slate-200 dark:border-slate-800 rounded-lg p-3 bg-slate-100/40 dark:bg-slate-900/40">
      <div className="flex items-center gap-1.5 mb-2 text-cyan-400">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <div className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-slate-500">{title}</div>
      </div>
      {children}
    </section>
  );
}
